import { dateISORegExp } from "common/utilities/regexp/regexp";
import type * as I from "core/api/coreApiClient";
import { parseISO } from "date-fns";
import { format as dateFnsFormat, toZonedTime } from "date-fns-tz";
import Timezones from "timezones.json";

export const apiDateFormat = "yyyy-MM-dd";

export function formatDatetime(datetimeISO: string, configuration: I.AppConfiguration) {
  return dateFnsFormat(new Date(datetimeISO), configuration.format_pattern_date_with_time);
}

export function parseAndFormatDatetime(datetime: string, configuration: I.AppConfiguration) {
  try {
    if (!datetime.match(dateISORegExp) && !datetime.match(/\s/)) return undefined;

    const parsedDate = new Date(datetime);
    if (parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())) {
      return dateFnsFormat(parsedDate, configuration.format_pattern_date_with_time);
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
}

export function formatDate(datetimeISO: string, configuration: I.AppConfiguration) {
  return dateFnsFormat(new Date(datetimeISO), configuration.format_pattern_date);
}

export function getBrowserTimeZone() {
  // https://stackoverflow.com/a/34602679/117191
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format an ISO date string (according to given timezone) and then remove the timezone mention
 */
export function toISOStringWithoutTimeZone(datetimeISO: string, timeZone: string) {
  const date = toZonedTime(datetimeISO, timeZone);
  return dateFnsFormat(date, "yyyy-MM-dd HH:mm:ss", { timeZone }).replace(" ", "T");
}

/**
 * Convert a given datetime to another while changing timeZone but keeping the initial time value
 * Example: 2011-12-03T10:15:30+5:00 > 2011-12-03T10:15:30+2:00
 */
export function toDateWithGivenTimeZone(datetimeISO: Date, srcTimeZone: string, destTimeZone: string) {
  // Zone the UTC date
  const date = toZonedTime(datetimeISO, srcTimeZone);
  const parsedDate = parseISO(date.toISOString());
  // Format to date time without timezone information
  const datetime = dateFnsFormat(parsedDate, "yyyy-MM-dd HH:mm:ss", { timeZone: srcTimeZone }).replace(" ", "T");
  // Convert destTimeZone name to offset based on the entered date (a date entered in summer or winter have a different offset)
  const tz = dateFnsFormat(parsedDate, "XXX", { timeZone: destTimeZone });
  return new Date(`${datetime}${tz}`);
}

export function getTimeZoneGroup(timeZone: string) {
  return Timezones.find((tzGroup) => tzGroup.utc.includes(timeZone));
}

/**
 * Ensure that a string is either following a given format or an ISO format.
 * Otherwise ensure the given object is a date object.
 */
export function isValidDate(displayFormat: string, date: string | Date) {
  let formattedDate = date;
  if (typeof formattedDate === "string") {
    if (!formattedDate.match(dateISORegExp) && formattedDate.length !== displayFormat.length) return false;
    formattedDate = new Date(date);
  }
  return !Number.isNaN(formattedDate.getTime());
}

export function configurationFormatPatternDateEdit(pattern: string) {
  let separator = "-";
  if (pattern.match("/")) separator = "/";

  const elements = pattern.split(separator);

  try {
    // DMY OR MDY
    if (elements[2].match(/[y]/i)) {
      // MDY
      if (elements[0].match(/[ml]/i)) {
        return ["MM", "dd", "yyyy"].join(separator);
      }
      // DMY
      if (elements[1].match(/[ml]/i)) {
        return ["dd", "MM", "yyyy"].join(separator);
      }
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}

  // YMD
  return ["yyyy", "MM", "dd"].join(separator);
}

export function configurationFormatPatternDateTimeEdit(pattern: string) {
  return `${configurationFormatPatternDateEdit(pattern)} HH:mm:ss`;
}

/** Check if a given date value is formatted following configuration date or datetime patterns */
export function dateValueFormat(value: string, configuration: I.AppConfiguration) {
  try {
    const dateValue = new Date(value);

    if (dateFnsFormat(dateValue, configuration.format_pattern_date).toLowerCase() === value.toLowerCase()) {
      return configuration.format_pattern_date;
    }

    if (dateFnsFormat(dateValue, configuration.format_pattern_date_with_time).toLowerCase() === value.toLowerCase()) {
      return configuration.format_pattern_date_with_time;
    }

    return undefined;
  } catch (e) {
    return undefined;
  }
}
